import React, { useEffect, useState, useCallback } from 'react';
import { Form, Row, Col, Alert, Modal, Button, Card, ProgressBar, Badge } from 'react-bootstrap';
import { 
  getStudentsBySchoolId,
  getClassesBySchoolId,
  getIndividualStudentResults,
  getAllAnswersByAssessmentInstance,
  getDeletedAnswersByAssessmentInstance
} from "../services/APIClient";

const RetakeScreeningComparisonModal = ({ show, onHide, schoolid }) => {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Selection state
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedOption, setSelectedOption] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  
  // State for available options (assessment instances with retakes)
  const [availableOptions, setAvailableOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // New state for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'original' or 'retake'
  const [isProcessingDefault, setIsProcessingDefault] = useState(false);

  // Helper function to get grade label from class
  const getGradeLabelFromClass = (classItem) => {
    if (!classItem) return "Unknown Grade";
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const loadClasses = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📚 DEBUG: Loading classes for school:', schoolid);
      const classesResponse = await getClassesBySchoolId(schoolid);
      
      const formattedClasses = (classesResponse || []).map(classItem => {
        const gradeLabel = getGradeLabelFromClass(classItem);
        
        return {
          id: classItem.id,
          name: classItem.name,
          grade: gradeLabel,
          displayName: `${classItem.name} (${gradeLabel})`
        };
      }).sort((a, b) => {
        // Sort by grade level first, then by name
        const gradeOrder = {
          "Junior Infants": 1,
          "Senior Infants": 2,
          "1st Class": 3,
          "2nd Class": 4,
          "3rd Class": 5,
          "4th Class": 6,
          "5th Class": 7,
          "6th Class": 8,
          "Unknown Grade": 9
        };
        
        const gradeA = gradeOrder[a.grade] || 9;
        const gradeB = gradeOrder[b.grade] || 9;
        
        if (gradeA !== gradeB) {
          return gradeA - gradeB;
        }
        
        return a.name.localeCompare(b.name);
      });

      console.log('📚 DEBUG: Formatted classes:', formattedClasses);
      setClasses(formattedClasses);
    } catch (err) {
      console.error('Error loading classes:', err);
      setError('Failed to load classes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [schoolid]);

  const loadStudentsForClass = useCallback(async (classId) => {
    if (!classId) {
      setStudents([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('👥 DEBUG: Loading students for class:', classId);
      // Get all students for the school then filter by class
      const studentsResponse = await getStudentsBySchoolId(schoolid);
      
      // Filter students by selected class
      const classStudents = (studentsResponse || []).filter(student => 
        student.classId && student.classId.toString() === classId.toString()
      );
      
      const formattedStudents = classStudents.map(student => {
        return {
          id: student.id,
          name: student.name,
          firstName: student.firstName,
          lastName: student.lastName,
          classId: student.classId,
          className: student.class?.name || 'Unknown Class',
          grade: student.class ? getGradeLabelFromClass(student.class) : 'Unknown Grade'
        };
      }).sort((a, b) => {
        // Sort alphabetically by display name
        const nameA = (a.firstName && a.lastName) ? `${a.firstName} ${a.lastName}` : a.name || '';
        const nameB = (b.firstName && b.lastName) ? `${b.firstName} ${b.lastName}` : b.name || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });

      console.log('👥 DEBUG: Formatted students for class:', formattedStudents);
      setStudents(formattedStudents);
    } catch (err) {
      console.error('Error loading students for class:', err);
      setError('Failed to load students for this class. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [schoolid]);

  const getAvailableOptionsForStudent = async (studentId) => {
    console.log('🔍 DEBUG START: Getting retake options for student:', studentId);
    
    if (!studentId) {
      console.log('❌ DEBUG: Missing student ID - returning empty array');
      return [];
    }

    try {
      console.log('📡 DEBUG: Fetching student assessment results...');
      
      // Get all assessments for the student
      const studentAssessments = await getIndividualStudentResults(studentId);
      console.log('📡 DEBUG: Student assessments response:', studentAssessments);

      if (!studentAssessments || studentAssessments.length === 0) {
        console.log('❌ DEBUG: No assessments found for student');
        return [];
      }

      // Filter out RTI assessments - only include standard screeners
      const standardScreeners = studentAssessments.filter(assessment => {
        if (assessment.rti === true) {
          console.log('🚫 DEBUG: Excluding RTI assessment:', assessment.name);
          return false;
        }
        return true;
      });

      console.log('📋 DEBUG: Standard screeners after RTI filter:', standardScreeners);

      // Return all standard assessment names (regardless of whether they have retakes)
      // The retake comparison will handle cases where no deleted answers exist
      const finalOptions = standardScreeners.map(assessment => assessment.name);
      
      console.log('✅ DEBUG: Final options returned (all standard assessments):', finalOptions);
      console.log('🔍 DEBUG END: Completed processing for student:', studentId);
      
      return finalOptions;

    } catch (error) {
      console.error('💥 DEBUG: Error in getAvailableOptionsForStudent:', error);
      return [];
    }
  };

  const loadAvailableOptions = useCallback(async (studentId) => {
    console.log('🚀 DEBUG: loadAvailableOptions called with:', {
      studentId,
      studentIdType: typeof studentId
    });
    
    setLoadingOptions(true);
    try {
      console.log('🚀 DEBUG: About to call getAvailableOptionsForStudent...');
      const options = await getAvailableOptionsForStudent(studentId);
      console.log('🚀 DEBUG: getAvailableOptionsForStudent returned:', options);
      setAvailableOptions(options);
      console.log('🚀 DEBUG: Set availableOptions state to:', options);
    } catch (error) {
      console.error('💥 DEBUG: Error in loadAvailableOptions:', error);
      setAvailableOptions([]);
    } finally {
      setLoadingOptions(false);
      console.log('🚀 DEBUG: loadAvailableOptions completed');
    }
  }, []);

  const getData = async (studentId, assessmentName) => {
    if (!studentId || !assessmentName) {
      return null;
    }

    try {
      console.log('📊 DEBUG: getData called with:', {
        studentId,
        assessmentName
      });

      // First get the student's assessments to find the matching assessment instance
      const studentAssessments = await getIndividualStudentResults(studentId);
      
      // Filter to standard screeners only (exclude RTI)
      const standardScreeners = studentAssessments.filter(assessment => assessment.rti !== true);
      
      const matchingAssessment = standardScreeners.find(assessment => 
        assessment.name === assessmentName
      );

      if (!matchingAssessment) {
        return {
          error: `Assessment "${assessmentName}" not found for this student.`
        };
      }

      const assessmentInstanceId = matchingAssessment.assessmentInstanceId || matchingAssessment.id;
      console.log('📊 DEBUG: Found matching assessment instance:', assessmentInstanceId);

      // Get all answers (both active and deleted) for this assessment instance
      const allAnswers = await getAllAnswersByAssessmentInstance(assessmentInstanceId);
      console.log('📊 DEBUG: All answers retrieved:', allAnswers);

      const { activeAnswers, deletedAnswers } = allAnswers;

      // Get dates for display first
      const deletedDate = deletedAnswers && deletedAnswers.length > 0 ? 
        (deletedAnswers[0]?.dateDeleted || deletedAnswers[0]?.submitted) : null;
      const activeDate = activeAnswers && activeAnswers.length > 0 ? 
        activeAnswers[0]?.submitted : null;

      console.log('📊 DEBUG: Processing answers for comparison:', {
        activeCount: activeAnswers?.length || 0,
        deletedCount: deletedAnswers?.length || 0
      });

      // Handle case where there are no active answers
      if (!activeAnswers || activeAnswers.length === 0) {
        return {
          error: `No current answers found for "${assessmentName}".`
        };
      }

      // Format active answers (current/retaken attempts) for RIGHT SIDE
      const rightResults = activeAnswers.map(answer => ({
        taskName: answer.taskName || 'Unknown Task',
        score: Math.round((answer.score || 0) * 100),
        maxScore: 100,
        timeSpent: answer.duration || 0,
        taskOrder: answer.taskOrder || 0
      })).sort((a, b) => a.taskOrder - b.taskOrder);

      // Handle case where there are no deleted answers (no retakes)
      if (!deletedAnswers || deletedAnswers.length === 0) {
        return {
          left: {
            date: 'Original Attempt (No Data)',
            results: [],
            noData: true,
            isOriginal: true
          },
          right: {
            date: activeDate ? `Current Attempt (${new Date(activeDate).toLocaleDateString()})` : 'Current Attempt',
            results: rightResults,
            isRetake: false // Not actually a retake since no original was deleted
          },
          assessmentInstanceId: assessmentInstanceId
        };
      }

      // Format deleted answers (original attempts) for LEFT SIDE
      const leftResults = deletedAnswers.map(answer => ({
        taskName: answer.taskName || 'Unknown Task',
        score: Math.round((answer.score || 0) * 100),
        maxScore: 100,
        timeSpent: answer.duration || 0,
        taskOrder: answer.taskOrder || 0
      })).sort((a, b) => a.taskOrder - b.taskOrder);

      const result = {
        left: {
          date: deletedDate ? `Original Attempt (${new Date(deletedDate).toLocaleDateString()})` : 'Original Attempt',
          results: leftResults,
          isOriginal: true
        },
        right: {
          date: activeDate ? `Retake Attempt (${new Date(activeDate).toLocaleDateString()})` : 'Retake Attempt',
          results: rightResults,
          isRetake: true
        },
        assessmentInstanceId: assessmentInstanceId
      };

      console.log('📊 DEBUG: Final result object:', result);
      return result;

    } catch (error) {
      console.error('Error fetching comparison data:', error);
      return {
        error: `Failed to load retake comparison data: ${error.message || 'Unknown error'}`
      };
    }
  };

  // New function to handle setting default results
  const handleSetDefault = (attemptType) => {
    setConfirmAction(attemptType);
    setShowConfirmModal(true);
  };

  // New function to confirm setting default
  const confirmSetDefault = async () => {
    if (!confirmAction || !comparisonData || !selectedStudent || !selectedOption) {
      return;
    }

    setIsProcessingDefault(true);
    
    try {
      // Here you would call your API to set the default results
      // This is a placeholder - you'll need to implement the actual API call
      console.log('Setting default results:', {
        studentId: selectedStudent,
        assessmentName: selectedOption,
        attemptType: confirmAction,
        assessmentInstanceId: comparisonData.assessmentInstanceId
      });

      // Example API call (you'll need to implement this):
      // await setDefaultAssessmentResults(
      //   comparisonData.assessmentInstanceId, 
      //   confirmAction === 'original' ? 'deleted' : 'active'
      // );

      // Show success message
      alert(`Successfully set ${confirmAction === 'original' ? 'original' : 'retake'} attempt as default results for ${selectedStudent}`);
      
      // Optionally refresh the data
      const refreshedData = await getData(selectedStudent, selectedOption);
      setComparisonData(refreshedData);

    } catch (error) {
      console.error('Error setting default results:', error);
      alert('Failed to set default results. Please try again.');
    } finally {
      setIsProcessingDefault(false);
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  };

  // Event handlers
  const handleClassChange = (classId) => {
    console.log('🏫 DEBUG: handleClassChange called with:', classId);
    setSelectedClass(classId);
    setSelectedStudent('');
    setSelectedOption('');
    setComparisonData(null);
    setAvailableOptions([]);
    
    if (classId) {
      loadStudentsForClass(classId);
    } else {
      setStudents([]);
    }
  };

  const handleStudentChange = (studentId) => {
    console.log('👤 DEBUG: handleStudentChange called with:', studentId);
    setSelectedStudent(studentId);
    setSelectedOption('');
    setComparisonData(null);
  };

  const handleOptionChange = async (option) => {
    console.log('📝 DEBUG: handleOptionChange called with:', option);
    setSelectedOption(option);
    if (selectedStudent && option) {
      console.log('📝 DEBUG: Getting data for student:', selectedStudent, 'option:', option);
      const data = await getData(selectedStudent, option);
      console.log('📝 DEBUG: Received data:', data);
      setComparisonData(data);
    }
  };

  // Load classes when modal opens
  useEffect(() => {
    if (show && schoolid) {
      loadClasses();
    }
  }, [show, schoolid, loadClasses]);

  // Load available options when student changes
  useEffect(() => {
    console.log('🔄 DEBUG: useEffect triggered for loading options:', {
      selectedStudent,
      shouldLoad: selectedStudent
    });
    
    if (selectedStudent) {
      console.log('✅ DEBUG: Student selected, calling loadAvailableOptions...');
      loadAvailableOptions(selectedStudent);
    } else {
      console.log('❌ DEBUG: No student selected, clearing options');
      setAvailableOptions([]);
    }
  }, [selectedStudent, loadAvailableOptions]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedClass('');
      setSelectedStudent('');
      setSelectedOption('');
      setComparisonData(null);
      setAvailableOptions([]);
      setClasses([]);
      setStudents([]);
      setError(null);
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  }, [show]);

  // Render task result card
  const renderTaskResult = (task, side) => (
    <Card key={`${task.taskName}-${side}`} className="mb-2">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{task.taskName}</strong>
          <Badge variant={task.score >= 70 ? "success" : task.score >= 50 ? "warning" : "danger"}>
            {task.score}%
          </Badge>
        </div>
        <ProgressBar 
          now={task.score} 
          variant={task.score >= 70 ? "success" : task.score >= 50 ? "warning" : "danger"}
          className="mb-1"
        />
        <small className="text-muted">
          Time: {task.timeSpent}s
        </small>
      </Card.Body>
    </Card>
  );

  // Custom header content for explanation
  const renderCustomHeader = () => {
    return (
      <div className="mb-4">
        <Alert variant="info">
          <div className="d-flex align-items-center">
            <div className="me-3" style={{ fontSize: '1.5rem' }}>🔄</div>
            <div>
              <strong>Retake Screening Comparison Tool</strong>
              <p className="mb-0 mt-1">
                Compare original screening attempts with retaken attempts for standard screening points. 
                All standard (non-RTI) assessments will appear in the dropdown. If an assessment has been retaken, 
                you'll see the original vs retake comparison. If not retaken, you'll see the current attempt only.
                Use the ✓ buttons to set which attempt should be the student's default results.
              </p>
            </div>
          </div>
        </Alert>
        
        <Row>
          <Col md={6}>
            <div className="p-3 border rounded bg-light">
              <div className="d-flex align-items-center mb-2">
                <span className="me-2" style={{ fontSize: '1.2rem' }}>📝</span>
                <strong className="text-primary">Original Attempt</strong>
              </div>
              <small className="text-muted">
                Shows the deleted answers from the student's first attempt at the screening point
              </small>
            </div>
          </Col>
          <Col md={6}>
            <div className="p-3 border rounded bg-light">
              <div className="d-flex align-items-center mb-2">
                <span className="me-2" style={{ fontSize: '1.2rem' }}>🎯</span>
                <strong className="text-success">Retake Attempt</strong>
              </div>
              <small className="text-muted">
                Shows the current active answers from the student's retake attempt
              </small>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const selectedStudentName = students?.find(s => s.id.toString() === selectedStudent)?.name || '';
  const selectedClassName = classes?.find(c => c.id.toString() === selectedClass)?.displayName || '';

  // Show loading state
  if (loading) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Retake Screening Comparison</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCustomHeader()}
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading...</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Show error state
  if (error) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Retake Screening Comparison</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCustomHeader()}
          <div className="alert alert-danger">
            <h5>Error</h5>
            <p>{error}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const getStudentDisplayName = () => {
    const student = students?.find(s => s.id.toString() === selectedStudent);
    if (!student) return '';
    return student.firstName && student.lastName 
      ? `${student.firstName} ${student.lastName}` 
      : student.name;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl">
        <style jsx>{`
          .scrollable-results::-webkit-scrollbar {
            width: 8px;
          }
          
          .scrollable-results::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          
          .scrollable-results::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 4px;
          }
          
          .scrollable-results::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
          
          .scrollable-results {
            scrollbar-width: thin;
            scrollbar-color: #c1c1c1 #f1f1f1;
          }
          
          .scroll-indicator {
            position: sticky;
            bottom: 0;
            background: linear-gradient(transparent, rgba(248, 249, 250, 0.9));
            text-align: center;
            padding: 5px;
            font-size: 0.75rem;
            color: #6c757d;
          }

          .set-default-btn {
            padding: 4px 8px;
            font-size: 0.8rem;
            border-radius: 3px;
            border: 1px solid rgba(255,255,255,0.3);
            background: rgba(255,255,255,0.1);
            color: white;
            transition: background-color 0.2s;
          }

          .set-default-btn:hover {
            background: rgba(255,255,255,0.2);
            color: white;
          }

          .set-default-btn:focus {
            box-shadow: 0 0 0 2px rgba(255,255,255,0.5);
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title>Retake Screening Comparison</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCustomHeader()}
          
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Select Class:</strong></Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedClass} 
                  onChange={(e) => handleClassChange(e.target.value)}
                >
                  <option value="">Choose a class...</option>
                  {classes?.map(classItem => (
                    <option key={classItem.id} value={classItem.id}>
                      {classItem.displayName}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Select Student:</strong></Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedStudent} 
                  onChange={(e) => handleStudentChange(e.target.value)}
                  disabled={!selectedClass}
                >
                  <option value="">
                    {!selectedClass ? 'Select a class first...' : 'Choose a student...'}
                  </option>
                  {students?.map(student => {
                    const displayName = student.firstName && student.lastName 
                      ? `${student.firstName} ${student.lastName}` 
                      : student.name;
                    return (
                      <option key={student.id} value={student.id}>
                        {displayName}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Select Screening Point:</strong></Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedOption} 
                  onChange={(e) => handleOptionChange(e.target.value)}
                  disabled={!selectedStudent || loadingOptions}
                >
                  <option value="">
                    {loadingOptions ? 'Loading options...' : 'Choose a screening point...'}
                  </option>
                  {availableOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Control>
                {loadingOptions && (
                  <small className="text-muted">
                    <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                    Loading screening options...
                  </small>
                )}
              </Form.Group>
            </Col>

            <Col md={3}>
              {selectedStudentName && (
                <div className="mt-4 pt-2">
                  <strong>Comparing:</strong><br/>
                  <small className="text-muted">{selectedClassName}</small><br/>
                  {selectedStudentName}
                  {selectedOption && <><br/><strong>Focus:</strong> {selectedOption}</>}
                </div>
              )}
            </Col>
          </Row>

          {comparisonData?.error && (
            <div className="alert alert-warning">
              <strong>No Data Available:</strong> {comparisonData.error}
            </div>
          )}

          {comparisonData && !comparisonData.error && (
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header className="bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0">📝 Original Attempt</h5>
                        {comparisonData.left.date && <small>📅 {comparisonData.left.date}</small>}
                        {comparisonData.left.isOriginal && (
                          <div className="mt-1">
                            <small className="badge bg-light text-dark">📚 Deleted Answers</small>
                          </div>
                        )}
                      </div>
                      {!comparisonData.left.noData && (
                        <Button 
                          className="set-default-btn"
                          size="sm"
                          onClick={() => handleSetDefault('original')}
                          title="Set original attempt as default results"
                        >
                          ✓ Set Default
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body 
                    className="p-0 scrollable-results"
                    style={{ 
                      maxHeight: '500px', 
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <div className="p-3">
                      {comparisonData.left.noData ? (
                        <div className="alert alert-info mb-0">
                          <strong>No original data available</strong> for this screening point
                        </div>
                      ) : comparisonData.left.results && comparisonData.left.results.length > 0 ? (
                        <>
                          {comparisonData.left.results.map(task => renderTaskResult(task, 'left'))}
                          <div className="mt-2 pt-2 border-top">
                            <small className="text-muted">
                              <strong>Total tasks:</strong> {comparisonData.left.results.length}
                              {comparisonData.left.isOriginal && <span className="ms-2 text-primary">📚 From original attempt</span>}
                            </small>
                          </div>
                          {comparisonData.left.results.length > 5 && (
                            <div className="scroll-indicator">
                              ↕️ Scroll to see all tasks
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-muted mb-0">No task results available</p>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <Card.Header className="bg-success text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0">🎯 Retake Attempt</h5>
                        {comparisonData.right.date && <small>📅 {comparisonData.right.date}</small>}
                        {comparisonData.right.isRetake && (
                          <div className="mt-1">
                            <small className="badge bg-light text-dark">🎯 Active Answers</small>
                          </div>
                        )}
                      </div>
                      {!comparisonData.right.noData && (
                        <Button 
                          className="set-default-btn"
                          size="sm"
                          onClick={() => handleSetDefault('retake')}
                          title="Set retake attempt as default results"
                        >
                          ✓ Set Default
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body 
                    className="p-0 scrollable-results"
                    style={{ 
                      maxHeight: '500px', 
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <div className="p-3">
                      {comparisonData.right.noData ? (
                        <div className="alert alert-info mb-0">
                          <strong>No retake data available</strong> for this screening point
                        </div>
                      ) : comparisonData.right.results && comparisonData.right.results.length > 0 ? (
                        <>
                          {comparisonData.right.results.map(task => renderTaskResult(task, 'right'))}
                          <div className="mt-2 pt-2 border-top">
                            <small className="text-muted">
                              <strong>Total tasks:</strong> {comparisonData.right.results.length}
                              {comparisonData.right.isRetake && <span className="ms-2 text-success">🎯 From retake attempt</span>}
                            </small>
                          </div>
                          {comparisonData.right.results.length > 5 && (
                            <div className="scroll-indicator">
                              ↕️ Scroll to see all tasks
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-muted mb-0">No task results available</p>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
          {comparisonData && !comparisonData.error && (
            <Button variant="primary">Export Comparison</Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Confirm Set Default Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning">
            <h6 className="alert-heading">Are you sure you want to proceed?</h6>
            <p className="mb-2">
              You are about to set the <strong>{confirmAction === 'original' ? 'original attempt' : 'retake attempt'}</strong> 
              as the default results for:
            </p>
            <ul className="mb-2">
              <li><strong>Student:</strong> {getStudentDisplayName()}</li>
              <li><strong>Assessment:</strong> {selectedOption}</li>
              <li><strong>Class:</strong> {selectedClassName}</li>
            </ul>
            <p className="mb-0">
              <strong>This action will:</strong>
            </p>
            <ul className="mb-2">
              <li>
                {confirmAction === 'original' 
                  ? 'Restore the original attempt results as the active/default results'
                  : 'Keep the retake attempt results as the active/default results'
                }
              </li>
              <li>Update the student's official assessment record</li>
              <li>Affect how these results appear in reports and dashboards</li>
            </ul>
            <hr />
            <p className="mb-0 text-muted">
              <em>Note: This change will be reflected immediately in the student's assessment history and cannot be easily undone.</em>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowConfirmModal(false)}
            disabled={isProcessingDefault}
          >
            Cancel
          </Button>
          <Button 
            variant={confirmAction === 'original' ? 'primary' : 'success'}
            onClick={confirmSetDefault}
            disabled={isProcessingDefault}
          >
            {isProcessingDefault ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Processing...
              </>
            ) : (
              <>
                ✓ Yes, Set {confirmAction === 'original' ? 'Original' : 'Retake'} as Default
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RetakeScreeningComparisonModal;